import React, { useRef, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Divider,
  ListItemIcon,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Logout from "@mui/icons-material/Logout";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { useAuth } from "../context/AuthContext";
import "../styles/navbar.css";
import Grow from "@mui/material/Grow";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import LanguageIcon from "@mui/icons-material/Language";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import logo from "../images/LOGO.png";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const featureItems = [
  {
    label: "Motor de Reservas",
    route: "/booking-engine",
    icon: <EventAvailableIcon sx={{ color: "#903AF2", mr: 1 }} />,
  },
  {
    label: "Gestor de Canales",
    route: "/channel-manager",
    icon: <DeviceHubIcon sx={{ color: "#903AF2", mr: 1 }} />,
  },
  {
    label: "Sitio Web",
    route: "/website-builder",
    icon: <LanguageIcon sx={{ color: "#903AF2", mr: 1 }} />,
  },
  {
    label: "Pagos Online",
    route: "/payments",
    icon: <AttachMoneyIcon sx={{ color: "#903AF2", mr: 1 }} />,
  },
  {
    label: "Panel Administrativo",
    route: "/admin-panel",
    icon: <DashboardCustomizeIcon sx={{ color: "#903AF2", mr: 1 }} />,
  },
  {
    label: "Automatización",
    route: "/automation",
    icon: <AutoAwesomeMotionIcon sx={{ color: "#903AF2", mr: 1 }} />,
  },
];

function Navbar({ setSearchbarVisible, setSelectedCategory }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const megaMenuTimeoutRef = useRef(null);

  let firstChar = "";
  if (isAuthenticated && user?.username && user?.lastname) {
    firstChar =
      user.username.charAt(0).toUpperCase() +
      user.lastname.charAt(0).toUpperCase();
  }

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    setMegaMenuOpen(true);
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 170);
  };

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleLogout = () => logout();
  const handleAccountClick = () =>
    isAuthenticated ? setDrawerOpen(true) : navigate("/login");

  const drawer = (
    <Box>
      <Box
        sx={{ width: 280, p: 2 }}
        role="presentation"
        onClick={handleDrawerToggle}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: "#903AF2", width: 48, height: 48, mr: 2 }}>
            {isAuthenticated ? firstChar : <AccountCircle />}
          </Avatar>
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontFamily: "'Red Hat Display', sans-serif" }}
            >
              {isAuthenticated ? `Hola, ${user?.username}` : "Bienvenido"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {isAuthenticated ? (
          <List>
            <ListItem button onClick={() => navigate("/dashboard")}>
              <ListItemIcon>
                <DashboardCustomizeIcon sx={{ color: "#903AF2" }} />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button onClick={() => navigate("/pay")}>
              <ListItemIcon>
                <ShoppingCart sx={{ color: "#903AF2" }} />
              </ListItemIcon>
              <ListItemText primary="Mis Compras" />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <Logout sx={{ color: "#903AF2" }} />
              </ListItemIcon>
              <ListItemText primary="Cerrar Sesión" />
            </ListItem>
          </List>
        ) : (
          <List>
            <ListItem button onClick={() => navigate("/login")}>
              <ListItemText
                primary="Iniciar Sesión"
                sx={{ color: "#903AF2" }}
              />
            </ListItem>
            <ListItem button onClick={() => navigate("/register")}>
              <ListItemText primary="Crear Cuenta" sx={{ color: "#903AF2" }} />
            </ListItem>
          </List>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle2"
          fontWeight="bold"
          gutterBottom
          sx={{ fontFamily: "'Red Hat Display', sans-serif" }}
        >
          Accesos rápidos
        </Typography>
        <List>
          <ListItem button onClick={() => navigate("/precios")}>
            <ListItemText primary="Planes y precios" />
          </ListItem>

          {/* CTA también en mobile */}
          <ListItem
            button
            onClick={() =>
              (window.location.href = "https://wa.me/542604206967")
            }
          >
            <ListItemIcon>
              <WhatsAppIcon sx={{ color: "#EE964B" }} />
            </ListItemIcon>
            <ListItemText primary="Empecemos" />
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#ffffff",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar
          sx={{
            // ✅ padding lateral y contenido centrado con maxWidth
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 1200,
              mx: "auto",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Mobile */}
            <Box
              display={{ xs: "flex", lg: "none" }}
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Link to="/" style={{ textDecoration: "none" }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Ubika logo"
                  sx={{
                    height: 28,
                    objectFit: "contain",
                    cursor: "pointer",
                    filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.25))",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                />
              </Link>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* CTA compacta en mobile */}
                {!isAuthenticated && (
                  <Button
                    onClick={() =>
                      (window.location.href = "https://wa.me/542604206967")
                    }
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      fontFamily: "'Red Hat Display', sans-serif", // ✅ fuente unificada
                      bgcolor: "#EE964B",
                      borderRadius: 2,
                      px: 1.6,
                      py: 0.8,
                      boxShadow: "0 10px 18px rgba(0,0,0,0.14)",
                      "&:hover": { bgcolor: "#e3883b" },
                    }}
                  >
                    Demo
                  </Button>
                )}

                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{ color: "#903AF2" }}
                >
                  <MenuIcon fontSize="large" />
                </IconButton>
              </Box>
            </Box>

            {/* Desktop */}
            <Box
              display={{ xs: "none", lg: "flex" }}
              alignItems="center"
              gap={3}
            >
              <Link to="/" style={{ transformOrigin: "top center" }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Ubika logo"
                  sx={{
                    height: 32,
                    objectFit: "contain",
                    cursor: "pointer",
                    filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.30))",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                />
              </Link>

              {/* Mega menu */}
              <ClickAwayListener onClickAway={() => setMegaMenuOpen(false)}>
                <Box
                  onMouseEnter={handleMegaMenuEnter}
                  onMouseLeave={handleMegaMenuLeave}
                  sx={{ position: "relative" }}
                >
                  <Button
                    endIcon={<ExpandMoreIcon />}
                    sx={{
                      color: "#903AF2",
                      fontWeight: 900,
                      fontFamily: "'Red Hat Display', sans-serif", // ✅ unificada
                      fontSize: "1rem",
                      textTransform: "none",
                      borderRadius: 2,
                      px: 1.4,
                      "&:hover": {
                        color: "#702AC2",
                        bgcolor: "rgba(144,58,242,0.06)",
                      },
                    }}
                  >
                    Plataforma
                  </Button>

                  <Grow
                    in={megaMenuOpen}
                    timeout={200}
                    style={{ transformOrigin: "top center" }}
                  >
                    <Paper
                      elevation={6}
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        zIndex: 20,
                        width: 720,
                        px: 3,
                        py: 2.5,
                        mt: 1.5,
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 1.5,
                        borderRadius: 3,
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        boxShadow:
                          "0 22px 44px rgba(0,0,0,0.14), 0 0 24px rgba(144,58,242,0.10)",
                        overflow: "hidden",
                      }}
                    >
                      {/* pequeño accent arriba */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: 0,
                          height: 6,
                          background:
                            "linear-gradient(90deg, #EE964B 0%, #FFE735 35%, #EAF7CF 100%)",
                        }}
                      />

                      {featureItems.map(({ label, route, icon }) => (
                        <Button
                          key={route}
                          onClick={() => {
                            setMegaMenuOpen(false);
                            navigate(route);
                          }}
                          fullWidth
                          startIcon={icon}
                          sx={{
                            justifyContent: "flex-start",
                            fontSize: "0.95rem",
                            color: "#565254",
                            textTransform: "none",
                            fontFamily: "'Red Hat Display', sans-serif", // ✅ unificada
                            fontWeight: 800,
                            backgroundColor: "transparent",
                            borderRadius: 2,
                            py: 1.1,
                            "&:hover": {
                              backgroundColor: "rgba(144,58,242,0.06)",
                              color: "#903AF2",
                            },
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Paper>
                  </Grow>
                </Box>
              </ClickAwayListener>

              <Button
                onClick={() => navigate("/precios")}
                sx={{
                  color: "#903AF2",
                  fontWeight: 900,
                  fontSize: "1rem",
                  fontFamily: "'Red Hat Display', sans-serif", // ✅ unificada
                  textTransform: "none",
                  borderRadius: 2,
                  px: 1.4,
                  "&:hover": { bgcolor: "rgba(144,58,242,0.06)" },
                }}
              >
                Precios
              </Button>
            </Box>

            {/* Right side */}
            <Box display="flex" alignItems="center" sx={{ gap: 1.5 }}>
              {/* CTA en desktop (siempre visible) */}
              {!isAuthenticated && (
                <Button
                  onClick={() =>
                    (window.location.href = "https://wa.me/542604206967")
                  }
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    display: { xs: "none", lg: "flex" },
                    textTransform: "none",
                    fontWeight: 900,
                    fontFamily: "'Red Hat Display', sans-serif",
                    bgcolor: "#EE964B",
                    borderRadius: 2,
                    px: 2.2,
                    py: 1,
                    boxShadow: "0 12px 22px rgba(0,0,0,0.14)",
                    "&:hover": { bgcolor: "#e3883b" },
                  }}
                >
                  Empecemos
                </Button>
              )}

              {isAuthenticated ? (
                <Box display={"flex"} alignItems={"center"}>
                  <Button
                    variant="contained"
                    size="small"
                    component={Link}
                    to="/dashboard"
                    startIcon={<DashboardIcon />}
                    sx={{
                      display: { xs: "none", lg: "flex" },
                      fontFamily: "'Red Hat Display', sans-serif",
                      fontWeight: 900,
                      ml: 1,
                      textTransform: "none",
                      backgroundColor: "#903AF2",
                      "&:hover": { backgroundColor: "#7029b3" },
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      boxShadow: "0 10px 18px rgba(0,0,0,0.12)",
                    }}
                  >
                    Dashboard
                  </Button>

                  <IconButton onClick={handleAccountClick} sx={{ ml: 1 }}>
                    <Avatar sx={{ width: 34, height: 34 }}>{firstChar}</Avatar>
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleAccountClick}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    border: "1px solid rgba(144,58,242,0.50)",
                    backgroundColor: "#fff",
                    color: "#903AF2",
                    fontWeight: 900,
                    textTransform: "none",
                    fontFamily: "'Red Hat Display', sans-serif", // ✅ unificada
                    px: 2.2,
                    py: 0.95,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "rgba(144,58,242,0.06)",
                      borderColor: "#903AF2",
                    },
                  }}
                >
                  Ingresar
                </Button>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{ "& .MuiDrawer-paper": { boxSizing: "border-box", width: 300 } }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default Navbar;
